export type PortalBranding = {
  site_name?: string | null;
  primary_color?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
  font_stack?: string | null;
  custom_css?: string | null;
  meta_description?: string | null;
  social_image_url?: string | null;
};

export type BrandingLookup = {
  branding: PortalBranding | null;
  tenant_slug: string | null;
};

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(res.statusText);
  const body = (await res.json()) as { data?: T };
  return (body.data ?? ({} as T)) as T;
}

export async function loadBrandingForCurrentHost(): Promise<BrandingLookup> {
  const host = typeof window !== "undefined" ? window.location.host : "";
  if (!host) return { branding: null, tenant_slug: null };
  try {
    const res = await fetch(`/api/v1/public-surface/branding/by-host?host=${encodeURIComponent(host)}`);
    return await unwrap<BrandingLookup>(res);
  } catch {
    return { branding: null, tenant_slug: null };
  }
}

export async function loadBrandingForSlug(slug: string): Promise<BrandingLookup> {
  const safe = (slug || "").trim().toLowerCase();
  if (!safe) return { branding: null, tenant_slug: null };
  try {
    const res = await fetch(`/api/v1/public-surface/branding/by-slug?slug=${encodeURIComponent(safe)}`);
    return await unwrap<BrandingLookup>(res);
  } catch {
    return { branding: null, tenant_slug: null };
  }
}

const STYLE_ID = "actionary-portal-branding";

export function applyBranding(branding: PortalBranding | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!branding) {
    document.getElementById(STYLE_ID)?.remove();
    return;
  }
  if (branding.primary_color) {
    root.style.setProperty("--actionary-primary", branding.primary_color);
  }
  if (branding.font_stack) {
    root.style.setProperty("--actionary-font", branding.font_stack);
  }
  if (branding.site_name) {
    document.title = branding.site_name;
  }
  if (branding.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = branding.favicon_url;
  }
  if (branding.meta_description) {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = branding.meta_description;
  }
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = branding.custom_css ?? "";
}
